import { z } from "zod";

/** 予定 1 件(SPEC §6.5)。保存せず、表示のたびに Google から取得する。 */
export const calendarEventViewSchema = z.object({
  id: z.string(),
  title: z.string(),
  start: z.iso.datetime(),
  end: z.iso.datetime(),
  allDay: z.boolean(),
  url: z.url().nullable(),
  meetUrl: z.url().nullable(),
  location: z.string().nullable(),
  /** 勤務場所の予定なら、その場所。通常の予定は null。 */
  workingLocation: z
    .object({ kind: z.enum(["HOME", "OFFICE", "CUSTOM"]), label: z.string() })
    .nullable()
    .default(null),
  /** 本人が「参加しない」と返事した予定。 */
  declined: z.boolean().default(false),
  /** 添付ファイル(Meet の議事録・文字起こし・録画など)。名前とリンクだけ。 */
  attachments: z
    .array(z.object({ title: z.string(), url: z.url(), mimeType: z.string().nullable() }))
    .default([]),
  /** どのカレンダーの予定か(calendars の id)。 */
  calendarId: z.string().default(""),
});
export type CalendarEventView = z.infer<typeof calendarEventViewSchema>;

/** 表示中のカレンダー(予定の色分けと凡例に使う)。failed は、取得できなかったもの(共有が外れた等)。 */
export const shownCalendarSchema = z.object({
  id: z.string(),
  name: z.string(),
  /** Google 側の色(#rrggbb)。 */
  color: z.string(),
  /** 自分のメインのカレンダーか(予定を ZENK レッドで出す。2026-10-07)。 */
  primary: z.boolean().optional(),
  failed: z.boolean().default(false),
});
export type ShownCalendar = z.infer<typeof shownCalendarSchema>;

/**
 * 1 週間(月曜〜日曜、日本時間)の予定。disabled: 連携機能が無効(カードを出さない)。
 * disconnected: 本人がまだ許可していない。connected: weekStart は月曜の日付(YYYY-MM-DD)。
 * events は、その週に掛かる予定すべて(週をまたぐ予定も含む)。日ごとに分けるのは画面側。
 */
export const calendarWeekResponseSchema = z.discriminatedUnion("status", [
  z.object({ status: z.literal("disabled") }),
  z.object({ status: z.literal("disconnected") }),
  z.object({
    status: z.literal("connected"),
    weekStart: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    events: z.array(calendarEventViewSchema),
    calendars: z.array(shownCalendarSchema).default([]),
  }),
]);
export type CalendarWeekResponse = z.infer<typeof calendarWeekResponseSchema>;

/** 選べるカレンダー 1 件。FREE_BUSY は、予定の有無だけが共有されている(詳細は読めない)。 */
export const selectableCalendarSchema = z.object({
  id: z.string(),
  name: z.string(),
  color: z.string(),
  primary: z.boolean(),
  accessRole: z.enum(["FREE_BUSY", "READER", "WRITER", "OWNER"]),
  selected: z.boolean(),
});
export type SelectableCalendar = z.infer<typeof selectableCalendarSchema>;

export const calendarListResponseSchema = z.discriminatedUnion("status", [
  z.object({ status: z.literal("disabled") }),
  z.object({ status: z.literal("disconnected") }),
  z.object({ status: z.literal("connected"), calendars: z.array(selectableCalendarSchema) }),
]);
export type CalendarListResponse = z.infer<typeof calendarListResponseSchema>;

/** 表示するカレンダーを選ぶ(1 件以上、20 件まで)。 */
export const updateCalendarSelectionRequestSchema = z.object({
  calendarIds: z.array(z.string().min(1).max(320)).min(1).max(20),
});
export type UpdateCalendarSelectionRequest = z.infer<typeof updateCalendarSelectionRequestSchema>;
